import { MinsterConfirmDialogComponent } from '../ui-components/minster-confirm-dialog/minster-confirm-dialog.component';
import { MinsterInjuryDialogComponent } from '../ui-components/minster-injury-dialog/minster-injury-dialog.component';
import { MinsterTextAreaComponent } from "../ui-components/minster-text-area/minster-text-area.component";
import { MinsterShowHideComponent } from "../ui-components/minster-show-hide/minster-show-hide.component";
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { ControlContainer, FormsModule, NgForm, NgModelGroup, Validators } from '@angular/forms';
import { MinsterInputComponent } from "../ui-components/minster-input/minster-input.component";
import { MinsterCardComponent } from '../ui-components/minster-card/minster-card.component';
import { AfterViewInit, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { injuryMatrix, injuryTypeMatrix } from '../../models/injury-matrix-map.model';
import { ScreenType, InputType, PortalEnum } from '../../models/enums.models';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { Injury, InjuryDetail } from '../../models/claim.model';
import { ClaimsService } from '../../services/claims.service';
import { DialogModel } from '../../models/dialog-model.model';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { LookUp } from '../../models/look-up.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'app-your-injuries-details',
    standalone: true,
    imports: [MinsterCheckboxComponent, MinsterShowHideComponent, MinsterInputComponent, MinsterSelectComponent,
    FormsModule, MatExpansionModule, MatButtonModule, MatIconModule, MinsterCardComponent, MinsterTextAreaComponent],
    templateUrl: './your-injuries-details.component.html',
    styleUrls: ['./your-injuries-details.component.css'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class YourInjuriesDetailsComponent extends ScreenBaseComponent implements AfterViewInit {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _injury: Injury;
    _injuryTypes: LookUp[];
    _bodyAreas: LookUp[];
    _bodyParts: LookUp[];
    _readonly: boolean = false;
    _inputTypes = InputType;
    _injuryMatrix = injuryMatrix;
    _injuryTypeMatrix = injuryTypeMatrix;

    constructor(
        stateService: StateService,
        router: Router,
        toastr: ToastrService,
        claimService: ClaimsService,
        private dialog: MatDialog,
        private change: ChangeDetectorRef
    ) {
        super(router, ScreenType.YourInjuriesDetails, toastr, stateService, claimService);
        this._injury = stateService.claim!.injury!;
        this._readonly = stateService.formIsSubmitted;
        this._bodyAreas = stateService.lookUps?.lookUpDictionary['AreaOfBody']!;
        this._bodyParts = stateService.lookUps?.lookUpDictionary['BodyPart']!;
        this._injuryTypes = stateService.lookUps?.lookUpDictionary['InjuryType']!;
    }

    ngAfterViewInit(): void { }

    override isScreenValid(): boolean {
        return this.stateService.getFormValidationErrorsExcludeRequired(this._formGroup!.control) == 0;
    }

    getInjuryDetailDescription(detail: InjuryDetail): string {
        const bodyPart = this.stateService.getLooUpValue('BodyPart', detail.bodyPartId);
        const injuryType = this.stateService.getLooUpValue('InjuryType', detail.injuryTypeId);
        return `${bodyPart} - ${injuryType}`;
    }

    onAddInjury(): void {
        if (this._injury.injuryDetails.length >= 20) {
            this.toastr.warning('You can only add up to 20 injuries.');
            return;
        }

        this.stateService.claim?.injury.injuryDetails.push(this.createNewInjuryDetail(this._injury.injuryId));
        this._injury = this.stateService.claim!.injury!;
    }

    onRemoveInjury(injury: InjuryDetail): void {
        const message = `Are you sure you want to delete this injury?`;

        this.dialog.open(MinsterConfirmDialogComponent, {
            maxWidth: "600px",
            data: new DialogModel('Confirm Deletion', message)
        }).afterClosed().subscribe(result => {
            if (result) {
                this.stateService.claim!.injury.injuryDetails = this.stateService.claim!.injury.injuryDetails.filter(item => item !== injury);
                this._injury = this.stateService.claim!.injury!;
                this.change.markForCheck();
            }
        });
    }

    createNewInjuryDetail(injuryId: number): InjuryDetail {
        return {
            injuryId: injuryId,
            injuryDetailId: 0,
            bodyPartId: 0,
            injuryTypeId: 0,
            areaOfBodyId: 0,
            preExistingInjury: '',
            accordionOpen: true,
            filteredBodyParts: this.filterBodyParts(0),
            filteredInjuryTypes: this.filterInjuryTypes(0)
        };
    }

    ngOnInit(): void {
        this._injury = this.stateService.claim!.injury!;
        this.initializeInjuryDetails();
    }

    override onNext(): void {
        if(this._readonly){
            super.onNext();
            return;
        }
        let hasErrors = false;
        this.updateValidators();

        if (this._formGroup && this._formGroup.invalid) {
            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);

                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];

                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            this._injury.injuryDetails.forEach((injuryDetail, index) => {
                const areaOfBodyControl = this._formGroup!.control.get(`area-of-body${index}`);
                const bodyPartControl = this._formGroup!.control.get(`body-part-${index}`);
                const injuryTypeControl = this._formGroup!.control.get(`injury-type-${index}`);

                [areaOfBodyControl, bodyPartControl, injuryTypeControl].forEach((control) => {
                    if (control && control.invalid) {
                        hasErrors = true;
                        control.markAsTouched();
                        control.markAsDirty();
                    }
                });
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        this.stateService.enableSaving = this.hasStateChanged();

        super.onNext();
    }

    initializeInjuryDetails(): void {
        this._injury.injuryDetails.forEach((injuryDetail) => {
            injuryDetail.filteredBodyParts = this.filterBodyParts(injuryDetail.areaOfBodyId);
            injuryDetail.filteredInjuryTypes = this.filterInjuryTypes(injuryDetail.bodyPartId);
        });
    }

    updateValidators(): void {
        const controls = [
            { formName: this._fcn.InjuryDetails.PreExistingConditionsDescription, makeRequired: this._injury.isInjuriesPreExisting, resetValue: '' },
            { formName: this._fcn.InjuryDetails.WhiplashSevereWhy, makeRequired: this._injury.isWhiplashSevere, resetValue: '' },
            { formName: this._fcn.InjuryDetails.CircumstancesExceptionalIfYesWhy, makeRequired: this._injury.isCircumstanceExceptional, resetValue: '' }        
        ];
        controls.forEach(({ formName, makeRequired, resetValue }) => {
            const control = this._formGroup?.control.get(formName);
            if (control) {
                if (makeRequired) {
                    control.setValidators([Validators.required]);
                } else {
                    control.clearValidators();
                    control.setValue(resetValue);
                }
                control.updateValueAndValidity();
            }
        });
    }

    filterBodyParts(areaOfBodyId: number): LookUp[] {
        const allowedBodyParts = this._injuryMatrix.bodyAreasToBodyParts[areaOfBodyId] || [];
        return this._bodyParts.filter(part => allowedBodyParts.includes(part.value!));
    }

    filterInjuryTypes(bodyPartId: number): LookUp[] {
        const allowedInjuryTypes = this._injuryMatrix.bodyPartsToInjuryTypes[bodyPartId] || [];
        return this._injuryTypes.filter(type => allowedInjuryTypes.includes(type.value!));
    }

    updateFilteredLookUps(index: number): void {
        const injuryDetail = this._injury.injuryDetails[index];
        injuryDetail.filteredBodyParts = this.filterBodyParts(injuryDetail.areaOfBodyId);
        injuryDetail.filteredInjuryTypes = this.filterInjuryTypes(injuryDetail.bodyPartId);
    }

    onAreaOfBodyChange(index: number): void {
        const injuryDetail = this._injury.injuryDetails[index];
        const allowedBodyParts = this.filterBodyParts(injuryDetail.areaOfBodyId);

        if (allowedBodyParts.some(part => part.value === injuryDetail.bodyPartId)) {
            injuryDetail.filteredBodyParts = allowedBodyParts;
            return;
        }

        injuryDetail.bodyPartId = 0;
        injuryDetail.injuryTypeId = 0;
        injuryDetail.filteredBodyParts = allowedBodyParts;
        injuryDetail.filteredInjuryTypes = [];
    }

    onBodyPartChange(index: number): void {
        const injuryDetail = this._injury.injuryDetails[index];
        const allowedInjuryTypes = this.filterInjuryTypes(injuryDetail.bodyPartId);

        if (allowedInjuryTypes.some(type => type.value === injuryDetail.injuryTypeId)) {
            injuryDetail.filteredInjuryTypes = allowedInjuryTypes;
            return;
        }

        injuryDetail.injuryTypeId = 0;
        injuryDetail.filteredInjuryTypes = allowedInjuryTypes;
    }

    onInjuryTypeChange(index: number): void {
        const injuryDetail = this._injury.injuryDetails[index];

        if (injuryDetail) {
            this.showInjuryPopupMessageAndSetPortalTrack(injuryDetail.injuryTypeId, injuryDetail.bodyPartId, index);
        }
    }

    requiresOther(index: number): boolean {
        const injuryDetail = this._injury.injuryDetails[index];
        return injuryDetail?.injuryTypeId === 20;
    }

    otherRequired(index: number): boolean {
        const injuryDetail = this._injury.injuryDetails[index];
        return injuryDetail?.injuryTypeId === 20 && this.stateService.formIsSubmitted && !injuryDetail.injuryTypeOther?.trim();
    }

    showInjuryPopupMessageAndSetPortalTrack(injuryTypeId: number, bodyPartId: number, index: number): void {
        let message = '';
        var portalTrack = PortalEnum.OIC;

        switch (injuryTypeId) {
            case 2:
                message = 'Was the injury caused by Fire/Flame/Hot Surface/Chemicals?';
                break;
            case 6:
            case 4:
                message = 'Please confirm if the client\'s vision, hearing, or memory has been affected. Do they have any bruises, abrasions, cuts, or scarring not already mentioned? If so, please capture the relevant "Injury Type". If the client was unconscious at any time during the accident, please capture details within Injury Symptoms/Additional Information.';
                break;
            case 12:
                message = 'Please confirm if the client\'s vision, hearing, or memory has been affected. Do they have any bruises, abrasions, cuts, or scarring not already mentioned? If so, please capture the relevant "Injury Type".';
                break;
            case 18:
            case 9:
            case 15:
            case 16:
            case 26:
            case 28:
            case 29:
                message = 'Has the client fully recovered?';
                break;
            case 17:
                message = 'Has there been loss or damage to the front teeth?';
                break;
            case 36:
                message = 'Was it for longer than 2 minutes?';
                break;
            case 24:
                message = 'Has the client been diagnosed with Tinnitus?';
                this.showMessage(message, (diagnosed: boolean) => {
                    if (!diagnosed) {
                        this.showMessage('Has the client\'s hearing fully recovered?', (recovered: boolean) => {
                            if (!recovered) {
                                portalTrack = PortalEnum.MultiTrack;
                                this.showOkMessage(
                                    'As tinnitus has not yet been diagnosed but the client is still suffering with symptoms, this will be classified as Hearing impaired - Ongoing/senses affected.'
                                );

                                this._injury.injuryDetails[index].injuryTypeId = 27;
                                this._injury = { ...this._injury };
                                this.change.markForCheck();
                            } 
                            else {
                                this.showOkMessage(
                                    'As Tinnitus has not been diagnosed and the client has made a full recovery, this will be classified as Hearing Impaired - Temporary/Senses Affected.'
                                );
                                this._injury.injuryDetails[index].injuryTypeId = 26;
                                this._injury = { ...this._injury };
                                this.change.markForCheck();
                            }
                        });
                    } else {
                        portalTrack = PortalEnum.MultiTrack;
                        this.showOkMessage('These are the next steps that will happen because x and y.');
                    }
                });
                return;
            case 31:
                message = 'Are the client\'s senses affected?';
                break;
            default:
                if ((bodyPartId === 14 || bodyPartId === 4) && injuryTypeId === 7) {
                    message = 'Is the size of the cut over 2cm and did the client need stitches?';
                } 
                else if ((bodyPartId === 1 || bodyPartId === 2 || bodyPartId === 3 || bodyPartId === 4) && injuryTypeId === 1) {
                    message = 'Note: If the client has banged their head during the accident, please confirm if the client\'s vision, hearing, or memory has been affected. Has concussion been confirmed by a medical professional, have they suffered any cuts or scarring not already mentioned? If so, please capture the relevant "Injury Type".';
                } 
                else if ((bodyPartId === 1 || bodyPartId === 2 || bodyPartId === 3) && injuryTypeId === 7) {
                    message = 'Is the size of the cut over 2cm and did the client need stitches?';
                }
                break;
        }

        if (message) {
            this.showMessage(message, (confirmed: boolean) => {
                if (confirmed) {
                    if((injuryTypeId == 2 &&            //Burns
                        (bodyPartId === 17 
                            || bodyPartId === 8 
                            || bodyPartId === 2 
                            || bodyPartId === 5 
                            || bodyPartId === 11 
                            || bodyPartId === 18 
                            || bodyPartId === 14
                            || bodyPartId === 1
                            || bodyPartId === 10
                            || bodyPartId === 3
                            || bodyPartId === 12
                            || bodyPartId === 15
                            || bodyPartId === 16
                            || bodyPartId === 7
                            || bodyPartId === 4
                            || bodyPartId === 13
                            || bodyPartId === 6 
                            || bodyPartId === 19
                            || bodyPartId === 9))
                        || (injuryTypeId == 7 &&        //Cuts or Scarring
                        (bodyPartId === 2
                            || bodyPartId === 14
                            || bodyPartId === 1
                            || bodyPartId === 3
                            || bodyPartId === 4))
                        || (injuryTypeId == 8)          //Dislocation
                        || (injuryTypeId == 11)         //Fracture or break           
                        || (injuryTypeId == 39)         //Fractured Skull
                        || (injuryTypeId == 27)         //Hearing Impacted - Ongoing
                        || (injuryTypeId == 17)         //Loss of Teeth
                        || (injuryTypeId == 36)         //Lost Consciousness
                        || (injuryTypeId == 31)         //Transient Eye Injury 
                    ){
                        portalTrack = PortalEnum.MultiTrack;
                    }

                    this.showOkMessage('These are the next steps that will happen because x and y.');
                }
                else {
                    if(injuryTypeId == 26           //Hearing Impacted - Temporary
                        || injuryTypeId == 15       //Loss of smell - Temporary
                        || injuryTypeId == 16       //Loss of taste - Temporary
                        || injuryTypeId == 18       //Memory Loss - Temporary
                        || injuryTypeId == 21       //Ohter phsychological
                        || injuryTypeId == 37       //Pre / Post Accident Amnesia
                        || injuryTypeId == 34       //Retinal Detachment
                        || injuryTypeId == 38       //Seizures and Fits
                        || injuryTypeId == 9        //Vision Impacted - Temporary
                    )              
                    {
                        portalTrack = PortalEnum.MultiTrack;
                    }

                    if(injuryTypeId == 17           //Loss of Teeth
                        || injuryTypeId == 36       //Lost Consciousness
                        || injuryTypeId == 20       //Ohther
                    ){             
                        portalTrack = PortalEnum.MOJ;
                    }

                    if (injuryTypeId in injuryTypeMatrix.injuryTypeMapping) {
                        this._injury.injuryDetails[index].injuryTypeId = injuryTypeMatrix.injuryTypeMapping[injuryTypeId];
                        this._injury = { ...this._injury };
                        this.change.markForCheck();
                    }
                }
            });
        }
        else{
            if(injuryTypeId == 43 
                || injuryTypeId == 3 
                || injuryTypeId == 6 
                || injuryTypeId == 13 
                || injuryTypeId == 28 
                || injuryTypeId == 29 
                || injuryTypeId == 30 
                || injuryTypeId == 33 
                || injuryTypeId == 10){
                portalTrack = PortalEnum.MultiTrack;
            }

            if(injuryTypeId == 20){
                portalTrack = PortalEnum.MOJ;
            }
        }

        this._injury.injuryDetails[index].claimTypeId = portalTrack;
        this._injury = { ...this._injury };
    }

    showMessage(message: string, callback: (confirmed: boolean) => void): void {
        this.dialog.open(MinsterInjuryDialogComponent, {
            maxWidth: '600px',
            data: new DialogModel('Potential Serious Injury', message)
        }).afterClosed().subscribe(result => {
            console.log('Dialog closed with result:', result);
            callback(result === true);
        });
    }

    showOkMessage(message: string): void {
        this.dialog.open(MinsterInjuryDialogComponent, {
            maxWidth: '600px',
            data: new DialogModel('Information', message)
        }).afterClosed().subscribe(() => {
            console.log('OK dialog closed.');
        });
    }
}